import React, { useEffect, useMemo, useState } from "react"
import _ from "lodash"
import { Button, IconButton, LinearProgress, Tooltip } from "@mui/material"
import {
    Add,
    DeleteOutline,
    EditOutlined,
    EventRepeatOutlined,
    LockOutlined,
    ReceiptLongOutlined,
    SchoolOutlined
} from "@mui/icons-material"

import { GetCurrentUser } from "../utils/hooks"
import { ConfirmationAlert } from "../components/index"
import { useSnackbar } from "../components/Snakebar"
import ClassList from "../components/ClassList"
import EmptyState from "../Lectures/components/EmptyState"
import FeeItemDialog from "./components/FeeItemDialog"
import { useLazyGetInstitutionClassesQuery, useUpdateClassMetadataMutation } from "../Redux/actions/classSetup.action"
import { useLazyGetPlatformRolesQuery } from "../Redux/actions/setting.action"

const buttonSx = { textTransform: "none", borderRadius: "10px", whiteSpace: "nowrap" }

/* Both lists live in the class's meta_data as [{ feeName, feeAmount }]. */
const KINDS = {
    monthlyFee: {
        label: "Monthly fee",
        title: "Monthly fees",
        description: "Charged every month. Collected from the Fee page.",
        icon: <EventRepeatOutlined fontSize="small" />,
        tone: "bg-indigo-50 text-indigo-600"
    },
    feeStructure: {
        label: "One-time fee",
        title: "One-time fees",
        description: "Admission and other one-off charges. Collected from Class Students.",
        icon: <ReceiptLongOutlined fontSize="small" />,
        tone: "bg-amber-50 text-amber-600"
    }
}

const formatMoney = (amount) => `₹${Number(amount || 0).toLocaleString("en-IN")}`

const feesOf = (classItem, kind) => {
    const list = _.get(classItem, ["meta_data", kind], [])
    return Array.isArray(list) ? list.filter((fee) => fee && fee.feeName) : []
}

const sumOf = (fees) => fees.reduce((total, fee) => total + Number(fee.feeAmount || 0), 0)

function FeeSection({ kind, fees, canManage, onAdd, onEdit, onDelete }) {
    const config = KINDS[kind]
    return (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <header className="flex flex-wrap items-center gap-3 px-4 py-4 sm:px-5">
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${config.tone}`}>
                    {config.icon}
                </span>
                <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-slate-900">{config.title}</h3>
                    <p className="text-sm text-slate-500">{config.description}</p>
                </div>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={onAdd}
                    disabled={!canManage}
                    sx={{ ...buttonSx, borderRadius: "8px" }}
                >
                    Add fee
                </Button>
            </header>

            {fees.length === 0 ? (
                <p className="border-t border-slate-100 px-5 py-6 text-center text-sm text-slate-400">
                    No {config.title.toLowerCase()} yet.
                </p>
            ) : (
                <>
                    <ul className="divide-y divide-slate-100 border-t border-slate-100">
                        {fees.map((fee) => (
                            <li key={fee.feeName} className="flex items-center gap-3 px-4 py-2.5 sm:px-5">
                                <span className="min-w-0 flex-1 truncate text-sm text-slate-800">{fee.feeName}</span>
                                <span className="text-sm font-semibold tabular-nums text-slate-900">
                                    {formatMoney(fee.feeAmount)}
                                </span>
                                {canManage && (
                                    <span className="-mr-1.5 flex">
                                        <Tooltip title="Edit fee" arrow>
                                            <IconButton size="small" aria-label={`Edit ${fee.feeName}`} onClick={() => onEdit(fee)}>
                                                <EditOutlined fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete fee" arrow>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                aria-label={`Delete ${fee.feeName}`}
                                                onClick={() => onDelete(fee)}
                                            >
                                                <DeleteOutline fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                    <footer className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm sm:px-5">
                        <span className="font-medium text-slate-600">
                            Total{kind === "monthlyFee" ? " per month" : ""}
                        </span>
                        <span className={`font-bold tabular-nums text-slate-900 ${canManage ? "sm:mr-[68px]" : ""}`}>
                            {formatMoney(sumOf(fees))}
                        </span>
                    </footer>
                </>
            )}
        </section>
    )
}

export default function PlatformFeeStructure() {
    const { showSnackbar } = useSnackbar()
    const user = useMemo(() => GetCurrentUser(), [])
    const schoolId = _.get(user, "meta_data.user_platform", null)

    const [activeClassId, setActiveClassId] = useState(null)
    // { kind, fee } — fee is null when adding
    const [editing, setEditing] = useState(null)
    const [deleting, setDeleting] = useState(null)

    const [getClasses, classesState] = useLazyGetInstitutionClassesQuery()
    const [getRoles, rolesState] = useLazyGetPlatformRolesQuery()
    const [updateClassMetadata, updateState] = useUpdateClassMetadataMutation()

    useEffect(() => {
        if (!schoolId) return
        getClasses({ institution_ref: schoolId })
        getRoles({ platform_id: schoolId })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const classes = useMemo(() => {
        const list = _.get(classesState, "data.list", [])
        return Array.isArray(list) ? list : []
    }, [classesState])

    const activeClass = useMemo(
        () => classes.find((item) => item.id === activeClassId) || classes[0] || null,
        [classes, activeClassId]
    )

    // Admins, and staff whose role can create content, may change fees
    const canManage = useMemo(() => {
        if (_.get(user, "meta_data.user_type") === "admin") return true
        const designation = _.get(user, "meta_data.designation")
        const roles = _.get(rolesState, "data.roles") || []
        const role = Array.isArray(roles) ? roles.find((item) => item.designation === designation) : null
        const privileges = _.get(role, "meta_data.privileges", [])
        return Array.isArray(privileges) && privileges.includes("Create Content")
    }, [user, rolesState])

    const monthlyFees = feesOf(activeClass, "monthlyFee")
    const oneTimeFees = feesOf(activeClass, "feeStructure")

    /* ------------------------------ saving ------------------------------ */

    const saveList = async (kind, list, successMessage) => {
        if (!activeClass) return false
        try {
            await updateClassMetadata({
                class_id: activeClass.id,
                meta_data: { ...(activeClass.meta_data || {}), [kind]: list }
            }).unwrap()
            showSnackbar({ message: successMessage, severity: "success" })
            getClasses({ institution_ref: schoolId })
            return true
        } catch {
            showSnackbar({ message: "Couldn't save the fee. Please try again.", severity: "error" })
            return false
        }
    }

    const submitFee = async ({ feeName, feeAmount }) => {
        const { kind, fee } = editing
        const list = feesOf(activeClass, kind)
        const next = fee
            ? list.map((item) => (item.feeName === fee.feeName ? { ...item, feeName, feeAmount } : item))
            : [...list, { feeName, feeAmount }]
        const saved = await saveList(kind, next, fee ? `${feeName} updated.` : `${feeName} added.`)
        if (saved) setEditing(null)
    }

    const confirmDelete = async () => {
        const { kind, fee } = deleting
        const next = feesOf(activeClass, kind).filter((item) => item.feeName !== fee.feeName)
        await saveList(kind, next, `${fee.feeName} removed.`)
        setDeleting(null)
    }

    /* ------------------------------- view ------------------------------- */

    const loadingClasses = classesState.isLoading
    const takenNames = editing
        ? feesOf(activeClass, editing.kind)
            .map((item) => item.feeName)
            .filter((name) => name !== editing.fee?.feeName)
        : []

    if (!loadingClasses && classes.length === 0) {
        return (
            <EmptyState
                icon={<SchoolOutlined />}
                title={classesState.isError ? "Couldn't load classes" : "No classes found"}
                description={
                    classesState.isError
                        ? "Please check your connection and try again."
                        : "Create a class first, then set up its fees here."
                }
                actionLabel={classesState.isError ? "Retry" : undefined}
                onAction={classesState.isError ? () => getClasses({ institution_ref: schoolId }) : undefined}
            />
        )
    }

    return (
        <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
            <aside className="lg:sticky lg:top-4 lg:self-start">
                <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-slate-700">Classes</h2>
                    <span className="text-xs tabular-nums text-slate-400">{classes.length}</span>
                </div>
                <ClassList
                    classes={classes}
                    activeId={activeClass?.id}
                    onSelect={(item) => setActiveClassId(item.id)}
                    loading={loadingClasses}
                    getMeta={(item) => {
                        const count = feesOf(item, "monthlyFee").length + feesOf(item, "feeStructure").length
                        return count > 0 ? `${count} fee${count === 1 ? "" : "s"}` : "No fees set"
                    }}
                />
            </aside>

            <div className="min-w-0 space-y-5">
                <div className="h-1 -mb-4">
                    {(classesState.isFetching || updateState.isLoading) && !loadingClasses && (
                        <LinearProgress sx={{ height: 3, borderRadius: 2 }} />
                    )}
                </div>

                {/* Class summary */}
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div className="min-w-0">
                        <h2 className="truncate text-xl font-semibold text-slate-900">
                            {activeClass?.class_name || "Select a class"}
                        </h2>
                        <p className="text-sm text-slate-500">
                            {monthlyFees.length + oneTimeFees.length} fee item
                            {monthlyFees.length + oneTimeFees.length === 1 ? "" : "s"}
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                            <div className="text-xs text-slate-500">Per month</div>
                            <div className="font-semibold tabular-nums text-slate-900">{formatMoney(sumOf(monthlyFees))}</div>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                            <div className="text-xs text-slate-500">One-time</div>
                            <div className="font-semibold tabular-nums text-slate-900">{formatMoney(sumOf(oneTimeFees))}</div>
                        </div>
                    </div>
                </div>

                {!canManage && !rolesState.isFetching && (
                    <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                        <LockOutlined fontSize="small" className="text-slate-400" />
                        You can view fees but not change them. Ask an admin for the "Create Content" permission.
                    </div>
                )}

                {loadingClasses ? (
                    <div className="space-y-5">
                        {[0, 1].map((key) => (
                            <div key={key} className="h-48 animate-pulse rounded-2xl bg-slate-100" />
                        ))}
                    </div>
                ) : (
                    ["monthlyFee", "feeStructure"].map((kind) => (
                        <FeeSection
                            key={kind}
                            kind={kind}
                            fees={kind === "monthlyFee" ? monthlyFees : oneTimeFees}
                            canManage={canManage}
                            onAdd={() => setEditing({ kind, fee: null })}
                            onEdit={(fee) => setEditing({ kind, fee })}
                            onDelete={(fee) => setDeleting({ kind, fee })}
                        />
                    ))
                )}
            </div>

            <FeeItemDialog
                open={Boolean(editing)}
                kindLabel={KINDS[editing?.kind]?.label || "Fee"}
                className={activeClass?.class_name}
                fee={editing?.fee || null}
                takenNames={takenNames}
                saving={updateState.isLoading}
                onClose={() => setEditing(null)}
                onSubmit={submitFee}
            />

            <ConfirmationAlert
                open={Boolean(deleting)}
                title="Delete fee?"
                message={`${deleting?.fee.feeName || "This fee"} will be removed from ${activeClass?.class_name || "the class"}. Payments already collected are not affected.`}
                onCancel={() => setDeleting(null)}
                onConfirm={updateState.isLoading ? undefined : confirmDelete}
            />
        </div>
    )
}
